import React, { useEffect, useMemo, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  Pressable,
  useWindowDimensions,
  type ViewStyle,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Check, Download, FileText, Minus } from 'lucide-react-native';
import {
  useTheme,
  SPACING,
  BORDER_RADIUS,
  TYPOGRAPHY,
  withWeight,
  SHELL_HEADER_HEIGHT,
  DESKTOP_CONTENT_MAX_WIDTH,
  CONTENT_MAX_WIDTH,
} from '../../src/theme';
import { Header } from '@components/layout';
import { Button, Card, EmptyState, ErrorBanner, Input, LoadingSpinner } from '@components/common';
import { SegmentedControl } from '@components/common/SegmentedControl';
import { useDesktopShell } from '@hooks/useDesktopShell';
import { useOwnPlansSubscription } from '@hooks/useOwnPlansSubscription';
import { useWebHover } from '@hooks/useWebHover';
import { CURSOR_POINTER } from '@utils/webStyles';
import { TAB_BAR_HEIGHT } from '@constants/navigationConfig';
import { useAuthStore } from '../../src/store/authStore';
import { useDefensePlansStore } from '../../src/store/defensePlansStore';
import { useFundingStore } from '../../src/store/fundingStore';
import { useServiceAreasStore } from '../../src/store/serviceAreasStore';
import { useUIStore } from '../../src/store/uiStore';
import { DEFENSE_STRATEGIES } from '../../src/components/map/defenseStrategies';
import { groupFeaturesForList } from '../../src/utils/planBundles';
import { newestPlan } from '../../src/utils/planGeometry';
import { featureWhoText, linksForFeature } from '../../src/utils/planAssignmentSummary';
import { KIND_LABEL, STATUS_META } from '../../src/utils/fundingLabels';
import {
  buildFundingRequestModel,
  featureSizeText,
  formatUsd,
} from '../../src/utils/fundingRequest';
import {
  downloadFundingRequestPdf,
  isPdfDownloadSupported,
} from '../../src/utils/fundingRequestPdf';
import type { DefensePlanFeature } from '../../src/types';

type CheckState = boolean | 'mixed';

// Desktop table columns after the name. The name takes the rest.
const COL_SIZE = 96;
const COL_WHO = 148;
const COL_COST = 128;
// Plan titles in the segmented control; longer ones are cut.
const SEGMENT_TITLE_MAX = 26;
const ASIDE_WIDTH = 340;
// Below this window width the sources panel drops under the table. Beside
// it, the fixed columns leave the step name under 180px and it cuts short.
const STACK_BELOW = 1280;
const CHECKBOX = 18;

const pluralize = (n: number, word: string) => `${n} ${word}${n === 1 ? '' : 's'}`;

function Section({
  label,
  first,
  children,
}: {
  label: string;
  first?: boolean;
  children: React.ReactNode;
}) {
  const { colors } = useTheme();
  return (
    <View style={first ? undefined : styles.sectionGap}>
      <Text style={[styles.overline, { color: colors.textSecondary }]}>{label}</Text>
      {children}
    </View>
  );
}

// The ink checkbox, or a dash when a bundle is part-checked.
function CheckMark({ state }: { state: CheckState }) {
  const { colors } = useTheme();
  const on = state !== false;
  return (
    <View
      style={[
        styles.checkbox,
        {
          borderColor: on ? colors.action : colors.border,
          backgroundColor: on ? colors.action : 'transparent',
        },
      ]}>
      {state === true && <Check size={12} color={colors.actionOn} />}
      {state === 'mixed' && <Minus size={12} color={colors.actionOn} />}
    </View>
  );
}

// One selectable row: an ink checkbox and up to two lines of text.
// `aria-checked` rather than accessibilityState, which RN-web drops. A picker
// marks its choice with the checkbox, never a row color.
function CheckRow({
  role,
  state,
  title,
  detail,
  ariaDetail,
  indent,
  quiet,
  tight,
  onPress,
  testID,
}: {
  role: 'checkbox' | 'radio';
  state: CheckState;
  title: string;
  detail?: string;
  // Full text for screen readers when the visible detail is shortened.
  ariaDetail?: string;
  indent?: boolean;
  // A member of a bundle: lighter title.
  quiet?: boolean;
  tight?: boolean;
  onPress: () => void;
  testID: string;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  const spoken = ariaDetail ?? detail;
  return (
    <Pressable
      onPress={onPress}
      testID={testID}
      accessibilityRole={role}
      aria-checked={state}
      accessibilityLabel={spoken ? `${title}, ${spoken}` : title}
      {...hoverProps}
      style={[
        styles.row,
        tight && styles.rowTight,
        indent && styles.rowIndent,
        CURSOR_POINTER,
        { backgroundColor: hovered ? colors.hoverSurface : undefined },
      ]}>
      <CheckMark state={state} />
      <View style={styles.rowText}>
        <Text
          numberOfLines={2}
          style={[quiet ? styles.rowTitleQuiet : styles.rowTitle, { color: colors.text }]}>
          {title}
        </Text>
        {detail ? (
          <Text numberOfLines={2} style={[styles.rowDetail, { color: colors.textSecondary }]}>
            {detail}
          </Text>
        ) : null}
      </View>
    </Pressable>
  );
}

// One row of the desktop table: checkbox, name, size and who in fixed
// columns, then the cost cell. The cost field is a sibling of the pressable
// part so typing in it never toggles the row. Members keep an empty cost
// cell so the columns line up.
function TableRow({
  state,
  title,
  size,
  who,
  ariaDetail,
  member,
  bundle,
  onPress,
  testID,
  cost,
}: {
  state: CheckState;
  title: string;
  size: string;
  who: string;
  ariaDetail: string;
  member?: boolean;
  bundle?: boolean;
  onPress: () => void;
  testID: string;
  cost?: React.ReactNode;
}) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();
  return (
    <View
      {...hoverProps}
      style={[
        styles.tableRow,
        member && styles.tableRowMember,
        { backgroundColor: hovered ? colors.hoverSurface : undefined },
      ]}>
      <Pressable
        onPress={onPress}
        testID={testID}
        accessibilityRole="checkbox"
        aria-checked={state}
        accessibilityLabel={ariaDetail ? `${title}, ${ariaDetail}` : title}
        style={[styles.tablePress, member && styles.tablePressMember, CURSOR_POINTER]}>
        <CheckMark state={state} />
        <Text
          numberOfLines={1}
          style={[
            styles.tableName,
            member
              ? TYPOGRAPHY.bodySmall
              : bundle
                ? withWeight(TYPOGRAPHY.body, 'medium')
                : TYPOGRAPHY.body,
            { color: colors.text },
          ]}>
          {title}
        </Text>
        <Text
          numberOfLines={1}
          style={[styles.tableSize, TYPOGRAPHY.bodySmall, { color: colors.textSecondary }]}>
          {size}
        </Text>
        <Text
          numberOfLines={2}
          style={[styles.tableWho, TYPOGRAPHY.bodySmall, { color: colors.textSecondary }]}>
          {who}
        </Text>
      </Pressable>
      <View style={styles.tableCost}>{cost}</View>
    </View>
  );
}

function TableHeader() {
  const { colors } = useTheme();
  const label = [styles.headLabel, { color: colors.textSecondary }];
  return (
    <View style={[styles.tableHead, { borderBottomColor: colors.border }]}>
      <View style={styles.tableHeadPress}>
        <View style={styles.checkboxSpace} />
        <Text style={[...label, styles.tableName]}>Step</Text>
        <Text style={[...label, styles.tableSize]}>Size</Text>
        <Text style={[...label, styles.tableWho]}>Who</Text>
      </View>
      <View style={styles.tableCost}>
        <Text style={label}>Estimated cost</Text>
      </View>
    </View>
  );
}

function CostField({
  value,
  onChange,
  testID,
  name,
  labelled,
}: {
  value: string;
  onChange: (text: string) => void;
  testID: string;
  name: string;
  // Phone shows the small label above the field; the desktop table has it
  // once, in the column header.
  labelled?: boolean;
}) {
  const input = (
    <Input
      compact
      label={labelled ? 'Estimated cost' : undefined}
      accessibilityLabel={`Estimated cost, ${name}`}
      value={value}
      onChangeText={(text) => onChange(text.replace(/[^0-9.]/g, ''))}
      keyboardType="numeric"
      placeholder="12000"
      containerStyle={styles.costField}
      testID={testID}
    />
  );
  return labelled ? <View style={styles.costWrap}>{input}</View> : input;
}

export default function FundingRequestScreen() {
  const { colors } = useTheme();
  const insets = useSafeAreaInsets();
  const { height: windowHeight, width: windowWidth } = useWindowDimensions();
  const tabBarVisible = useUIStore((s) => s.tabBarVisible);
  const isDesktopShell = useDesktopShell();
  const stacked = isDesktopShell && windowWidth < STACK_BELOW;
  const user = useAuthStore((s) => s.user);
  const ownPlans = useDefensePlansStore((s) => s.ownPlans);
  const plansLoaded = useOwnPlansSubscription();
  const partiesByPlan = useDefensePlansStore((s) => s.partiesByPlan);
  const assignmentsByPlan = useDefensePlansStore((s) => s.assignmentsByPlan);
  const sources = useFundingStore((s) => s.sources);
  const loadSources = useFundingStore((s) => s.load);
  const areas = useServiceAreasStore((s) => s.areas);
  const areaIds = areas.map((a) => a.id);
  const areaKey = areaIds.join(',');

  const [pickedPlanId, setPickedPlanId] = useState<string | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [costs, setCosts] = useState<Record<string, string>>({});
  const [pickedSources, setPickedSources] = useState<Set<string>>(new Set());
  const [otherSource, setOtherSource] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    void loadSources(areaIds);
    // areaKey stands in for areaIds, as on the Funding screen.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [areaKey, loadSources]);

  // Owner only, and only plans with something to fund.
  const plans = useMemo(
    () => ownPlans.filter((p) => !!user && p.ownerId === user.id && p.features.length > 0),
    [ownPlans, user]
  );
  const plan = plans.find((p) => p.id === pickedPlanId) ?? newestPlan(plans);
  const planId = plan?.id ?? null;

  // Parties and assignments are subscribed only while the plan editor is
  // open (useDefensePlanLayer.web.ts), so this page subscribes for the plan
  // it shows. On leave it lets go unless the editor has since opened and
  // needs them.
  useEffect(() => {
    if (!planId) return;
    const store = useDefensePlansStore.getState();
    store.subscribeOwnPlanParties(planId);
    store.subscribeOwnPlanAssignments(planId);
    return () => {
      if (useUIStore.getState().planPanelOpen) return;
      const latest = useDefensePlansStore.getState();
      latest.unsubscribeOwnPlanParties();
      latest.unsubscribeOwnPlanAssignments();
    };
  }, [planId]);

  const parties = useMemo(
    () => (planId ? (partiesByPlan[planId] ?? {}) : {}),
    [partiesByPlan, planId]
  );
  const assignments = useMemo(
    () => (planId ? (assignmentsByPlan[planId] ?? {}) : {}),
    [assignmentsByPlan, planId]
  );
  const entries = useMemo(
    () => (plan ? groupFeaturesForList(plan.features, plan.bundles ?? []) : []),
    [plan]
  );

  // A bundle's cost starts from the owner's estimate (Oct 2026). Still editable
  // and still unsaved here: this form never writes back to the plan.
  const planIdForCosts = plan?.id;
  useEffect(() => {
    const bundles = useDefensePlansStore
      .getState()
      .ownPlans.find((p) => p.id === planIdForCosts)?.bundles;
    if (!bundles) return;
    setCosts((prev) => {
      const next = { ...prev };
      for (const bundle of bundles) {
        if (bundle.estimate != null && next[bundle.id] === undefined) {
          next[bundle.id] = String(bundle.estimate);
        }
      }
      return next;
    });
  }, [planIdForCosts]);

  const choosePlan = (id: string) => {
    if (id === planId) return;
    setPickedPlanId(id);
    setSelected(new Set());
    setCosts({});
  };

  const toggleIds = (ids: string[], on: boolean) => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of ids) {
        if (on) next.add(id);
        else next.delete(id);
      }
      return next;
    });
  };

  const toggleSource = (id: string) => {
    setPickedSources((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const setCost = (key: string, text: string) => setCosts((prev) => ({ ...prev, [key]: text }));

  const stepSize = (feature: DefensePlanFeature) =>
    featureSizeText(feature) || feature.address || '';
  const stepWho = (feature: DefensePlanFeature) =>
    featureWhoText(linksForFeature(feature.id, assignments), parties) ?? 'Not assigned yet';
  const stepDetail = (feature: DefensePlanFeature) =>
    [stepSize(feature), stepWho(feature)].filter(Boolean).join(' · ');
  const stepLabel = (feature: DefensePlanFeature) =>
    DEFENSE_STRATEGIES[feature.strategy]?.label ?? feature.strategy;

  // Same rule as the PDF: a cost counts when something under its bundle or
  // loose step is checked.
  const summary = useMemo(() => {
    if (!plan) return { steps: 0, total: null as number | null };
    let total: number | null = null;
    for (const entry of entries) {
      const key = entry.type === 'bundle' ? entry.bundle.id : entry.feature.id;
      const members = entry.type === 'bundle' ? entry.members : [entry.feature];
      if (!members.some((m) => selected.has(m.id))) continue;
      const value = parseFloat(costs[key] ?? '');
      if (Number.isFinite(value) && value > 0) total = (total ?? 0) + value;
    }
    return { steps: plan.features.filter((f) => selected.has(f.id)).length, total };
  }, [plan, entries, selected, costs]);
  const summarySteps =
    summary.steps === 0 ? 'No steps selected' : `${pluralize(summary.steps, 'step')} selected`;
  const summaryTotal =
    summary.total === null ? null : `Estimated total ${formatUsd(summary.total)}`;

  const handleDownload = async () => {
    if (!plan || busy) return;
    setBusy(true);
    setError(null);
    try {
      const numeric: Record<string, number | null> = {};
      for (const [key, text] of Object.entries(costs)) {
        const value = parseFloat(text);
        numeric[key] = Number.isFinite(value) ? value : null;
      }
      const model = buildFundingRequestModel({
        plan,
        selectedFeatureIds: [...selected],
        costs: numeric,
        sources: sources.filter((s) => pickedSources.has(s.id)),
        otherSource,
        parties: Object.values(parties),
        assignments: Object.values(assignments),
      });
      await downloadFundingRequestPdf(model, plan);
    } catch {
      setError('The PDF could not be built. Try again.');
    } finally {
      setBusy(false);
    }
  };

  const columnWidth = isDesktopShell ? DESKTOP_CONTENT_MAX_WIDTH : CONTENT_MAX_WIDTH;
  const ready = !!plan && isPdfDownloadSupported;
  // Phone bar sits above the tab bar when it shows, else above the safe area.
  const barOffset = tabBarVisible ? TAB_BAR_HEIGHT + insets.bottom : insets.bottom;

  const renderCost = (key: string, name: string) => (
    <CostField
      value={costs[key] ?? ''}
      onChange={(text) => setCost(key, text)}
      testID={`funding-request-cost-${key}`}
      name={name}
      labelled={!isDesktopShell}
    />
  );

  const planPicker =
    plans.length > 1 ? (
      <Section label="Plan" first>
        {plans.length <= 3 ? (
          <SegmentedControl
            role="radiogroup"
            ariaLabel="Plan"
            testID="funding-request-plan"
            value={planId ?? ''}
            onChange={choosePlan}
            options={plans.map((p) => ({
              key: p.id,
              label:
                p.title.length > SEGMENT_TITLE_MAX
                  ? `${p.title.slice(0, SEGMENT_TITLE_MAX - 1).trimEnd()}\u2026`
                  : p.title,
            }))}
          />
        ) : (
          plans.map((p) => (
            <CheckRow
              key={p.id}
              role="radio"
              state={p.id === planId}
              title={p.title}
              detail={pluralize(p.features.length, 'step')}
              onPress={() => choosePlan(p.id)}
              testID={`funding-request-plan-${p.id}`}
            />
          ))
        )}
      </Section>
    ) : null;

  const renderEntries = () =>
    entries.map((entry) => {
      const lineStyle = [styles.group, { borderBottomColor: colors.border }];
      if (entry.type === 'feature') {
        const feature = entry.feature;
        const on = selected.has(feature.id);
        const name = stepLabel(feature);
        return (
          <View key={feature.id} style={lineStyle}>
            {isDesktopShell ? (
              <TableRow
                state={on}
                title={name}
                size={stepSize(feature)}
                who={stepWho(feature)}
                ariaDetail={stepDetail(feature)}
                onPress={() => toggleIds([feature.id], !on)}
                testID={`funding-request-step-${feature.id}`}
                cost={on ? renderCost(feature.id, name) : undefined}
              />
            ) : (
              <>
                <CheckRow
                  role="checkbox"
                  state={on}
                  title={name}
                  detail={stepDetail(feature)}
                  onPress={() => toggleIds([feature.id], !on)}
                  testID={`funding-request-step-${feature.id}`}
                />
                {on && renderCost(feature.id, name)}
              </>
            )}
          </View>
        );
      }
      const ids = entry.members.map((m) => m.id);
      const count = ids.filter((id) => selected.has(id)).length;
      const state: CheckState = count === 0 ? false : count === ids.length ? true : 'mixed';
      const bundleId = entry.bundle.id;
      const members = entry.members.map((member) => {
        const on = selected.has(member.id);
        return isDesktopShell ? (
          <TableRow
            key={member.id}
            member
            state={on}
            title={stepLabel(member)}
            size={stepSize(member)}
            who={stepWho(member)}
            ariaDetail={stepDetail(member)}
            onPress={() => toggleIds([member.id], !on)}
            testID={`funding-request-step-${member.id}`}
          />
        ) : (
          <CheckRow
            key={member.id}
            role="checkbox"
            indent
            quiet
            tight
            state={on}
            title={stepLabel(member)}
            detail={stepDetail(member)}
            onPress={() => toggleIds([member.id], !on)}
            testID={`funding-request-step-${member.id}`}
          />
        );
      });
      return (
        <View key={bundleId} style={lineStyle}>
          {isDesktopShell ? (
            <TableRow
              bundle
              state={state}
              title={entry.bundle.name}
              size={pluralize(ids.length, 'step')}
              who=""
              ariaDetail={pluralize(ids.length, 'step')}
              onPress={() => toggleIds(ids, state !== true)}
              testID={`funding-request-bundle-${bundleId}`}
              cost={count > 0 ? renderCost(bundleId, entry.bundle.name) : undefined}
            />
          ) : (
            <>
              <CheckRow
                role="checkbox"
                state={state}
                title={entry.bundle.name}
                detail={pluralize(ids.length, 'step')}
                onPress={() => toggleIds(ids, state !== true)}
                testID={`funding-request-bundle-${bundleId}`}
              />
              {count > 0 && renderCost(bundleId, entry.bundle.name)}
            </>
          )}
          {members}
        </View>
      );
    });

  const needs = (
    <Section label="What needs funding" first={plans.length <= 1}>
      {isDesktopShell && <TableHeader />}
      {renderEntries()}
    </Section>
  );

  const sourceRows = sources.map((source) => (
    <CheckRow
      key={source.id}
      role="checkbox"
      tight
      state={pickedSources.has(source.id)}
      title={source.name}
      detail={
        isDesktopShell
          ? `${source.funder} · ${STATUS_META[source.status].label}`
          : `${source.funder} · ${KIND_LABEL[source.kind]} · ${STATUS_META[source.status].label}`
      }
      ariaDetail={`${source.funder} · ${KIND_LABEL[source.kind]} · ${STATUS_META[source.status].label}`}
      onPress={() => toggleSource(source.id)}
      testID={`funding-request-source-${source.id}`}
    />
  ));

  const otherField = (
    <View style={isDesktopShell ? styles.otherWrapAside : styles.otherWrap}>
      <Input
        compact
        label="Another source"
        value={otherSource}
        onChangeText={setOtherSource}
        placeholder="County wildfire mitigation fund"
        maxLength={200}
        testID="funding-request-other"
        containerStyle={styles.otherField}
      />
    </View>
  );

  const summaryBlock = (
    <View style={styles.summaryText}>
      <Text style={[styles.summaryLine, { color: colors.text }]}>{summarySteps}</Text>
      {summaryTotal ? (
        <Text style={[styles.summaryTotal, { color: colors.text }]}>{summaryTotal}</Text>
      ) : null}
    </View>
  );

  const downloadButton = (
    <Button
      title="Download PDF"
      leftIcon={Download}
      size={isDesktopShell ? 'sm' : 'md'}
      fullWidth={isDesktopShell}
      loading={busy}
      disabled={selected.size === 0}
      onPress={() => void handleDownload()}
      testID="funding-request-download"
    />
  );

  const stickyStyle = {
    position: 'sticky',
    top: SPACING.md,
    maxHeight: Math.max(windowHeight - SHELL_HEADER_HEIGHT - SPACING.md * 2, 320),
  } as unknown as ViewStyle;

  return (
    <View
      style={[styles.container, { backgroundColor: colors.background }]}
      testID="funding-request-screen">
      <Header large showBack title="Funding request" contentMaxWidth={columnWidth} />
      <ScrollView
        style={styles.scroll}
        contentContainerStyle={[
          styles.content,
          { maxWidth: columnWidth },
          {
            paddingBottom: isDesktopShell
              ? SPACING.xl
              : ready
                ? barOffset + BAR_HEIGHT + SPACING.md
                : barOffset + SPACING.md,
          },
        ]}
        showsVerticalScrollIndicator={false}>
        {!!user && !user.isAnonymous && !plansLoaded && !plan ? (
          <LoadingSpinner />
        ) : !ready ? (
          <EmptyState
            icon={FileText}
            title="No defense plan to build from"
            message="Draw at least one defense on the map, then come back."
          />
        ) : isDesktopShell ? (
          <View style={stacked ? styles.columnsStacked : styles.columns}>
            <View style={stacked ? undefined : styles.mainColumn}>
              {planPicker}
              {needs}
            </View>
            <View style={stacked ? undefined : [styles.aside, stickyStyle]}>
              <Card variant="outlined" padding="md" style={styles.asideCard}>
                <ScrollView style={styles.asideScroll} showsVerticalScrollIndicator={false}>
                  <Section label="Possible sources" first>
                    {sourceRows}
                    {otherField}
                  </Section>
                </ScrollView>
                <View style={[styles.asideFooter, { borderTopColor: colors.border }]}>
                  {summaryBlock}
                  <ErrorBanner message={error} />
                  {downloadButton}
                </View>
              </Card>
            </View>
          </View>
        ) : (
          <>
            {planPicker}
            {needs}
            <Section label="Possible sources">
              {sourceRows}
              {otherField}
            </Section>
          </>
        )}
      </ScrollView>
      {ready && !isDesktopShell && (
        <View
          style={[
            styles.bar,
            {
              bottom: barOffset,
              backgroundColor: colors.background,
              borderTopColor: colors.border,
            },
          ]}>
          <View style={[styles.barInner, { maxWidth: columnWidth }]}>
            <ErrorBanner message={error} />
            <View style={styles.barRow}>
              {summaryBlock}
              {downloadButton}
            </View>
          </View>
        </View>
      )}
    </View>
  );
}

// Padding, one 44px button, padding, hairline.
const BAR_HEIGHT = SPACING.md * 2 + 44 + 1;

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  scroll: {
    flex: 1,
  },
  content: {
    width: '100%',
    alignSelf: 'center',
    padding: SPACING.md,
  },
  columns: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: SPACING.lg,
  },
  columnsStacked: {
    gap: SPACING.lg,
  },
  mainColumn: {
    flex: 1,
    minWidth: 0,
  },
  aside: {
    width: ASIDE_WIDTH,
  },
  asideCard: {
    flexShrink: 1,
    minHeight: 0,
  },
  asideScroll: {
    flexShrink: 1,
  },
  asideFooter: {
    marginTop: SPACING.md,
    paddingTop: SPACING.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    gap: SPACING.sm,
  },
  sectionGap: {
    marginTop: SPACING.lg,
  },
  overline: {
    ...TYPOGRAPHY.overline,
    marginBottom: SPACING.sm,
  },
  group: {
    borderBottomWidth: StyleSheet.hairlineWidth,
    paddingVertical: SPACING.xs,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    paddingHorizontal: SPACING.sm,
    paddingVertical: SPACING.sm,
    borderRadius: BORDER_RADIUS.md,
  },
  rowTight: {
    paddingVertical: SPACING.xs + SPACING.xxs,
  },
  rowIndent: {
    marginLeft: SPACING.lg,
  },
  checkbox: {
    width: CHECKBOX,
    height: CHECKBOX,
    borderRadius: BORDER_RADIUS.xs,
    borderWidth: 1.5,
    alignItems: 'center',
    justifyContent: 'center',
  },
  checkboxSpace: {
    width: CHECKBOX,
  },
  rowText: {
    flex: 1,
  },
  rowTitle: {
    ...TYPOGRAPHY.body,
  },
  rowTitleQuiet: {
    ...TYPOGRAPHY.bodySmall,
  },
  rowDetail: {
    ...TYPOGRAPHY.bodySmall,
  },
  tableHead: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: SPACING.sm,
    paddingBottom: SPACING.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
  },
  tableHeadPress: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
  },
  headLabel: {
    ...TYPOGRAPHY.caption,
  },
  tableRow: {
    flexDirection: 'row',
    alignItems: 'center',
    minHeight: 44,
    paddingHorizontal: SPACING.sm,
    borderRadius: BORDER_RADIUS.md,
  },
  tableRowMember: {
    minHeight: 36,
  },
  tablePress: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    alignSelf: 'stretch',
  },
  tablePressMember: {
    paddingLeft: SPACING.lg,
  },
  tableName: {
    flex: 1,
    minWidth: 0,
  },
  tableSize: {
    width: COL_SIZE,
  },
  tableWho: {
    width: COL_WHO,
  },
  tableCost: {
    width: COL_COST,
    paddingLeft: SPACING.sm,
    justifyContent: 'center',
  },
  costWrap: {
    paddingLeft: SPACING.sm + CHECKBOX + SPACING.sm,
    paddingRight: SPACING.sm,
    paddingBottom: SPACING.xs,
  },
  costField: {
    maxWidth: 160,
    marginBottom: 0,
  },
  otherWrap: {
    paddingHorizontal: SPACING.sm,
    paddingTop: SPACING.sm,
  },
  otherWrapAside: {
    paddingTop: SPACING.md,
  },
  otherField: {
    marginBottom: 0,
  },
  summaryText: {
    flex: 1,
  },
  summaryLine: {
    ...TYPOGRAPHY.body,
  },
  summaryTotal: {
    ...withWeight(TYPOGRAPHY.body, 'semibold'),
  },
  bar: {
    position: 'absolute',
    left: 0,
    right: 0,
    borderTopWidth: StyleSheet.hairlineWidth,
    paddingHorizontal: SPACING.md,
    paddingVertical: SPACING.md,
  },
  barInner: {
    width: '100%',
    alignSelf: 'center',
    gap: SPACING.sm,
  },
  barRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.md,
  },
});
